import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { HotelProviders } from '../constants';

// Returning a fresh object literal from a selector makes useSelector re-render on every
// dispatched action, so the "no search stored yet" fallback needs a stable reference.
const EMPTY_SEARCH_OBJ = {};

/**
 * Single source of truth for whether the agency exposes the
 * "Show non-preferred hotels & best available rates" option.
 * Agencies restricted to preferred rates hide that checkbox, so anything else offering a way to
 * search without preferred rate codes must respect the same configuration (CRUIS-2833).
 */
const useBestAvailableRatesOption = (tripId) => {
  const isHotelTripSearch = !tripId;
  const hotelSearchObj = useSelector((store) =>
    isHotelTripSearch
      ? store.tripSearch.searchInputs.hotel
      : store.hotel.hotelSearches[tripId]?.searchObj || EMPTY_SEARCH_OBJ,
  );
  const hotelRateCodes = useSelector((store) => store.user.hotelRateCodes);
  const { travelAgency = {}, isLoggedIn } = useSelector((store) => store.user);

  const doReDesignRateCodesFilter = travelAgency?.filterSettings?.doRedesignRateCodesFilter;

  const doShowSabreRateCodes = useMemo(() => {
    if (!hotelRateCodes?.length) return false;

    // If no hotel providers are specified or if Sabre Lodging is included, show the filter
    if (
      !hotelSearchObj ||
      !hotelSearchObj.hotelProvidersToInclude ||
      !hotelSearchObj.hotelProvidersToInclude.length ||
      hotelSearchObj.hotelProvidersToInclude?.includes(HotelProviders.SABRE_LODGING)
    ) {
      return true;
    }
    return false;
  }, [hotelSearchObj, hotelRateCodes]);

  const doHideRateCodePreferencesFilter = useMemo(() => {
    const filterSettings = travelAgency?.filterSettings;
    if (!filterSettings) return false;
    const { doHideHotelRateCodesFilter, doHideHotelRateCodesFilterForCFP } = filterSettings;

    if (doReDesignRateCodesFilter && !doShowSabreRateCodes) {
      return true;
    }
    return isLoggedIn ? !!doHideHotelRateCodesFilter : !!doHideHotelRateCodesFilterForCFP;
  }, [isLoggedIn, travelAgency.filterSettings, doShowSabreRateCodes, doReDesignRateCodesFilter]);

  const isBestAvailableRatesOptionEnabled = useMemo(
    () =>
      (!travelAgency.settings || !!travelAgency.settings.doShowPreferredRateCodesCheckbox) &&
      !doHideRateCodePreferencesFilter,
    [travelAgency.settings, doHideRateCodePreferencesFilter],
  );

  return {
    doShowSabreRateCodes,
    doHideRateCodePreferencesFilter,
    isBestAvailableRatesOptionEnabled,
  };
};

export default useBestAvailableRatesOption;
